import moment from 'moment';
import rejectPurchaseRequestTemplate from './modals/reject-purchase-request/reject-purchase-request.html';
import RejectPurchaseRequestModalController from './modals/reject-purchase-request/reject-purchase-request.controller';
import markProcessedTemplate from '../expense-review-details/modals/mark-processed/mark-processed.html';
import MarkProcessedModalController from '../expense-review-details/modals/mark-processed/mark-processed.controller';

class GeneralBusinessReviewDetailsController {

  constructor(CompanyService, toastr, $scope, $http, $rootScope, $stateParams, $state, $uibModal, TravelExpenseService, AuthenticationService) {
    'ngInject';

    this.moment = moment;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.toastr = toastr;
    this.state = $state;
    this.$stateParams = $stateParams;
    this.modal = $uibModal;
    this.apiHost = $rootScope.apiHost;
    this.travelExpenseService = TravelExpenseService;
    this.auth = AuthenticationService;
    this.$scope = $scope;
    this.receiptBusyKey = null;
    this.pendingReceiptAction = null;

    this.report = { expenses: [] };

    this.statusOptions = {
      approveButtonText: 'Approve',
      approveNextStatus: 'APPROVED',
      rejectButtonText: 'Reject',
      rejectNextStatus: 'REJECTED'
    };

    this.travelExpenseStatusDetails = {};

    this.loading = true;
    this.travelExpenseService.getExpenseReport($stateParams.expenseReportId).then((response) => {
      const report = this.travelExpenseService.normalizeExpenseReportDates(response.data);
      this.travelExpenseService.hydrateEstimateBasisDisplay(report);
      this.report = report;
      this.report.expenses = report.expenses || [];
      this.travelExpenseStatusDetails = this.travelExpenseService.getTravelExpenseStatusDetails(this.report.status);
      this.loading = false;
    }, () => {
      this.loading = false;
      this.toastr.error('Could not load the expense report.', 'Error');
    });

    $scope.$watch(() => this.travelExpenseStatusDetails, (newVal) => {
      if (!newVal) {
        return;
      }

      if (newVal.value === 'REIMBURSEMENT_REQUESTED' || newVal.value === 'REIMBURSEMENT_APPROVED') {
        this.statusOptions.approveButtonText = 'Approve Reimbursement';
        this.statusOptions.approveNextStatus = 'REIMBURSEMENT_APPROVED';
        this.statusOptions.rejectButtonText = 'Reject Reimbursement';
        this.statusOptions.rejectNextStatus = 'REIMBURSEMENT_REJECTED';
      } else {
        this.statusOptions.approveButtonText = 'Approve';
        this.statusOptions.approveNextStatus = 'APPROVED';
        this.statusOptions.rejectButtonText = 'Reject';
        this.statusOptions.rejectNextStatus = 'REJECTED';
      }
    });
  }

  getItems() {
    return (this.report.expenses || []).filter((expense) => !expense.itinerary);
  }

  getEstimatedTotal() {
    return this.getItems().reduce((total, expense) => total + (expense.amount || 0), 0);
  }

  getActualTotal() {
    return this.getItems().reduce((total, expense) => total + (expense.actualAmount || 0), 0);
  }

  formatDate(value) {
    return value ? moment(value).format('MMM DD, YYYY') : '';
  }

  isPostTravel() {
    return this.travelExpenseStatusDetails && this.travelExpenseStatusDetails.phase === 'Post-Travel';
  }

  getPaymentMethodLabel(expense) {
    if (!expense || !expense.paymentMethod) {
      return '—';
    }

    if (expense.paymentMethod === 'corporate') {
      return 'Corporate';
    }

    if (expense.paymentMethod === 'personal') {
      return 'Personal';
    }

    return expense.paymentMethod;
  }

  canManageReceipts() {
    return this.auth && this.auth.hasPermission('PERM_MANAGE_EMPLOYEE_EXPENSES');
  }

  receiptActionKey(expense, receipt) {
    const expenseId = expense && expense.id != null ? expense.id : 'new';
    const receiptId = receipt && (receipt.receipt || receipt.id) ? (receipt.receipt || receipt.id) : 'upload';
    return expenseId + ':' + receiptId;
  }

  isReceiptBusy(expense, receipt) {
    return this.receiptBusyKey === this.receiptActionKey(expense, receipt);
  }

  beginReplaceReceipt(expense, receipt) {
    if (!this.canManageReceipts() || !expense || !receipt || !receipt.receipt || this.receiptBusyKey) {
      return;
    }
    this.pendingReceiptAction = { mode: 'replace', expense, receipt };
    this.openAdminReceiptFilePicker();
  }

  beginUploadReceipt(expense) {
    if (!this.canManageReceipts() || !expense || this.receiptBusyKey) {
      return;
    }
    this.pendingReceiptAction = { mode: 'upload', expense, receipt: null };
    this.openAdminReceiptFilePicker();
  }

  openAdminReceiptFilePicker() {
    const input = document.getElementById('admin-receipt-file-input');
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  }

  onAdminReceiptFileSelected(fileList) {
    const run = () => {
      const file = fileList && fileList.length ? fileList[0] : null;
      const pending = this.pendingReceiptAction;
      this.pendingReceiptAction = null;
      if (!file || !pending || !pending.expense) {
        return;
      }

      if (pending.mode === 'replace') {
        this.replaceReceipt(pending.expense, pending.receipt, file);
        return;
      }

      this.uploadReceipt(pending.expense, file);
    };

    if (this.$scope.$$phase) {
      run();
    } else {
      this.$scope.$apply(run);
    }
  }

  replaceReceipt(expense, receipt, file) {
    if (!file || !expense || !receipt || !receipt.receipt) {
      return;
    }
    const busyKey = this.receiptActionKey(expense, receipt);
    this.receiptBusyKey = busyKey;
    this.travelExpenseService.replaceExpenseReceipt(
      this.report.id,
      expense.id,
      receipt.receipt,
      file,
      receipt.receiptKind
    ).then((response) => {
      this.applyExpenseReceipts(expense, response.data);
      this.toastr.success('Receipt replaced.', 'Updated');
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message)
        || 'Could not replace the receipt.';
      this.toastr.error(message, 'Error');
    }).finally(() => {
      if (this.receiptBusyKey === busyKey) {
        this.receiptBusyKey = null;
      }
    });
  }

  uploadReceipt(expense, file) {
    if (!file || !expense) {
      return;
    }
    const busyKey = this.receiptActionKey(expense, null);
    this.receiptBusyKey = busyKey;
    this.travelExpenseService.uploadExpenseReceiptAdmin(
      this.report.id,
      expense.id,
      file
    ).then((response) => {
      this.applyExpenseReceipts(expense, response.data);
      this.toastr.success('Receipt uploaded.', 'Updated');
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message)
        || 'Could not upload the receipt.';
      this.toastr.error(message, 'Error');
    }).finally(() => {
      if (this.receiptBusyKey === busyKey) {
        this.receiptBusyKey = null;
      }
    });
  }

  applyExpenseReceipts(expense, updatedExpense) {
    if (!expense || !updatedExpense) {
      return;
    }
    expense.receipts = updatedExpense.receipts || [];
    this.travelExpenseService.hydrateEstimateBasisDisplay(this.report);
  }

  cancel() {
    this.state.go('admin.expenses');
  }

  approveExpenseReport() {
    this.loading = true;
    this.travelExpenseService.updateExpenseReportStatus(this.report.id, this.statusOptions.approveNextStatus).then(() => {
      this.loading = false;
      this.toastr.success('Request approved.', 'Approved');
      this.state.go('admin.expenses');
    }, () => {
      this.loading = false;
      this.toastr.error('Could not approve the request.', 'Error');
    });
  }

  rejectExpenseReport() {
    this.showRejectExpenseDialog().then((rejectionReason) => {
      if (!rejectionReason) {
        return;
      }

      this.loading = true;
      this.travelExpenseService.updateExpenseReportStatus(
        this.report.id,
        this.statusOptions.rejectNextStatus,
        rejectionReason
      ).then(() => {
        this.loading = false;
        this.toastr.success('Request rejected.', 'Rejected');
        this.state.go('admin.expenses');
      }, () => {
        this.loading = false;
        this.toastr.error('Could not reject the request.', 'Error');
      });
    });
  }

  markExpenseReportAsProcessed() {
    this.showMarkProcessedDialog().then((result) => {
      if (!result || !result.processedDate) {
        return;
      }

      this.loading = true;
      this.travelExpenseService.updateExpenseReportStatus(
        this.report.id,
        'PROCESSED',
        null,
        result.processedDate,
        result.notify
      ).then(() => {
        this.loading = false;
        this.toastr.success('Request marked as processed.', 'Processed');
        this.state.go('admin.expenses');
      }, () => {
        this.loading = false;
        this.toastr.error('Could not mark the request as processed.', 'Error');
      });
    });
  }

  showRejectExpenseDialog() {
    const isReimbursement = this.statusOptions.rejectNextStatus === 'REIMBURSEMENT_REJECTED';

    return this.modal.open({
      template: rejectPurchaseRequestTemplate,
      controller: RejectPurchaseRequestModalController,
      controllerAs: '$ctrl',
      windowClass: 'hmodal-danger',
      resolve: {
        modalContext: () => ({
          reportName: this.report.name,
          isReimbursement
        })
      }
    }).result.then((reason) => reason, () => undefined);
  }

  showMarkProcessedDialog() {
    const reimbursementAmount = this.travelExpenseService.formatNumber(
      this.travelExpenseService.getEmployeeReimbursementTotal(this.report)
    );
    const totalCost = this.travelExpenseService.formatNumber(this.getActualTotal());

    return this.modal.open({
      template: markProcessedTemplate,
      controller: MarkProcessedModalController,
      controllerAs: '$ctrl',
      windowClass: 'hmodal-success',
      resolve: {
        modalContext: () => ({
          reportName: this.report.name,
          reimbursementAmount,
          totalCost
        })
      }
    }).result.then((result) => result, () => undefined);
  }
}

export default GeneralBusinessReviewDetailsController;



// WEBPACK FOOTER //
// ./client/pages/admin/general-business-review-details/general-business-review-details.controller.js